import { useTranslation } from 'react-i18next';

import type { PossibleDuplicate } from '@/api/people';
import { Button } from '@/components/ui/Button';

import { DuplicateMatches } from './DuplicateMatches';

interface Props {
  /** The matches, from the pre-check or from the 409 of `POST /people/` — the same payload. */
  duplicates: PossibleDuplicate[];
  /**
   * §6.4's **primary**: a second consideration on the card that already exists. This is ADR 0002's
   * whole point — *"one person, any number of considerations"* — so it is the first action offered
   * and it is the only one that keeps the earlier feedback findable.
   */
  onAddConsideration: (personId: number) => void;
  /**
   * §6.4's **secondary**: a genuinely different human. ADR 0014 keeps this path because two people
   * can legitimately share a key — a family email address, a father and son with the same name, an
   * agency's phone number — and it is the only call that carries the override.
   */
  onCreateSeparate: () => void;
  /** A write is in flight; §4 keeps the button in place and swaps its label for a spinner. */
  isBusy: boolean;
}

/**
 * `DESIGN.md` §6.4's duplicate interaction — **feedback protection #7 rendered as a choice, not as a
 * validation error.**
 *
 * > *"Ivan Petrov already exists (interviewed for Backend Engineer, March 2025). Add a new
 * > consideration to the existing card?"* — with that as the primary action and "create a separate
 * > person" as the secondary.
 *
 * ⚠️ **Red text under the name would be the protection deleted while looking implemented.** ADR 0002
 * splits `Person` from `Consideration` precisely so that the same human applying to a second vacancy
 * is a **second consideration on the existing card** — not a duplicate to refuse and not a second
 * card. An interface that says "this name is taken" and leaves the recruiter to work around it
 * inverts the ADR: it makes the *correct* outcome the awkward one. So the reachable card comes
 * first — a link to read it and a primary action to add the run to it — and creating a second person
 * is the secondary, which is the only place ADR 0014's override is sent.
 *
 * ⚠️ **What this file is now is §6.4's two actions and nothing else** (TASK-112). The presentation —
 * the sentence naming the person and their runs, the `matched_on` reason, the link to that card — moved
 * to `DuplicateMatches`, which §6.5's *Edit* renders too. The **actions** did not move and must not:
 * this primary is ADR 0002's answer to a *create* and there is no run to open from a person card, and
 * the secondary's word is *create*. §6.4's rendering is unchanged, byte for byte, which is why
 * `addCandidate.test.tsx` needed no edit.
 */
export function DuplicateWarning({
  duplicates,
  onAddConsideration,
  onCreateSeparate,
  isBusy,
}: Props) {
  const { t } = useTranslation('candidates');

  return (
    <DuplicateMatches
      duplicates={duplicates}
      heading={t('dialog.duplicate.heading')}
      // §6.4's primary, per match: it is a particular card the run is added to.
      actionFor={(duplicate) => (
        <Button size="sm" isLoading={isBusy} onClick={() => onAddConsideration(duplicate.id)}>
          {t('dialog.duplicate.addConsideration')}
        </Button>
      )}
    >
      {/*
       * §6.4's secondary, once: it is about the form, not about a particular match. It is **after**
       * every primary in the DOM as well as in style — a keyboard user reaches the action that keeps
       * one card first.
       */}
      <Button variant="secondary" size="sm" isLoading={isBusy} onClick={onCreateSeparate}>
        {t('dialog.duplicate.createSeparate')}
      </Button>
    </DuplicateMatches>
  );
}
